import { useLayoutEffect, useMemo, useRef } from 'react';
import { useFrame, useThree } from '@react-three/fiber';
import * as THREE from 'three';
import { useConfig, type TruckConfig } from '../../store';
import { easeOutCubic, Tween } from '../../motion';
import { fenderColor, wheelFinish } from './paints';

const PAINT_EASE_MS = 300;

/** Procedural greyscale roughness noise, tiled — breaks up flat specular on big panels. */
function noiseRough(base: number): THREE.CanvasTexture {
  const s = 256;
  const c = document.createElement('canvas');
  c.width = c.height = s;
  const g = c.getContext('2d')!;
  const id = g.createImageData(s, s);
  for (let i = 0; i < s * s; i++) {
    const v = base * 255 + (Math.random() - 0.5) * 22;
    id.data[i * 4] = id.data[i * 4 + 1] = id.data[i * 4 + 2] = v;
    id.data[i * 4 + 3] = 255;
  }
  g.putImageData(id, 0, 0);
  const t = new THREE.CanvasTexture(c);
  t.wrapS = t.wrapT = THREE.RepeatWrapping;
  t.repeat.set(4, 4);
  return t;
}

type PaintConfig = Pick<TruckConfig, 'bodyColor' | 'fenders' | 'wheels'>;

interface Finish {
  body: THREE.Color;
  accent: THREE.Color;
  rim: THREE.Color;
  rimMetalness: number;
  rimRoughness: number;
}

function targetFinish({ bodyColor, fenders, wheels }: PaintConfig): Finish {
  const rim = wheelFinish(wheels);
  return {
    body: new THREE.Color(bodyColor),
    accent: new THREE.Color(fenderColor(bodyColor, fenders)),
    rim: new THREE.Color(rim.color),
    rimMetalness: rim.metalness,
    rimRoughness: rim.roughness,
  };
}

function createMaterials(finish: Finish) {
  const paintRough = noiseRough(0.34);
  const rubberRough = noiseRough(0.9);
  return {
    paint: new THREE.MeshPhysicalMaterial({ color: finish.body, metalness: 0, roughness: 0.34, roughnessMap: paintRough, clearcoat: 0.8, clearcoatRoughness: 0.12, envMapIntensity: 1.0 }),
    accent: new THREE.MeshPhysicalMaterial({ color: finish.accent, metalness: 0, roughness: 0.36, clearcoat: 0.65, clearcoatRoughness: 0.14, envMapIntensity: 1.0 }),
    hatchPaint: new THREE.MeshStandardMaterial({ color: finish.body, roughness: 0.55, metalness: 0.0 }),
    rim: new THREE.MeshStandardMaterial({ color: finish.rim, metalness: finish.rimMetalness, roughness: finish.rimRoughness }),
    chrome: new THREE.MeshStandardMaterial({ color: 0xdadde3, roughness: 0.22, metalness: 1.0 }),
    chromeHi: new THREE.MeshStandardMaterial({ color: 0xe8eaf0, roughness: 0.12, metalness: 1.0 }),
    black: new THREE.MeshStandardMaterial({ color: 0x15171c, roughness: 0.5, metalness: 0.3 }),
    gasket: new THREE.MeshStandardMaterial({ color: 0x101216, roughness: 0.72 }),
    rubber: new THREE.MeshStandardMaterial({ color: 0x111114, roughness: 0.9, roughnessMap: rubberRough }),
    glass: new THREE.MeshPhysicalMaterial({ color: 0x95aec0, metalness: 0.06, roughness: 0.15, clearcoat: 1.0, clearcoatRoughness: 0.07, envMapIntensity: 2.4, emissive: 0x1e2e3c, emissiveIntensity: 0.55 }),
    glassDoor: new THREE.MeshPhysicalMaterial({ color: 0x7897aa, metalness: 0.06, roughness: 0.17, clearcoat: 1.0, clearcoatRoughness: 0.07, envMapIntensity: 1.8, emissive: 0x101b22, emissiveIntensity: 0.16 }),
    mirrorGlass: new THREE.MeshStandardMaterial({ color: 0xb9c7cf, metalness: 0.65, roughness: 0.14, envMapIntensity: 2.0 }),
    cabinDark: new THREE.MeshStandardMaterial({ color: 0x241a20, roughness: 0.9 }),
    seat: new THREE.MeshStandardMaterial({ color: 0x6a4658, roughness: 0.75 }),
    counter: new THREE.MeshStandardMaterial({ color: 0xe9ddd2, roughness: 0.5 }),
    wood: new THREE.MeshStandardMaterial({ color: 0xb98a5e, roughness: 0.6 }),
    bulb: new THREE.MeshStandardMaterial({ color: 0xfff2c8, emissive: 0xffcf7a, emissiveIntensity: 1.9, roughness: 0.4 }),
    glassGreen: new THREE.MeshPhysicalMaterial({ color: 0x1f5a35, roughness: 0.2, metalness: 0, clearcoat: 0.5 }),
    glassAmber: new THREE.MeshPhysicalMaterial({ color: 0x8a5a1e, roughness: 0.2, metalness: 0, clearcoat: 0.5 }),
    glassClear: new THREE.MeshPhysicalMaterial({ color: 0xdfeee6, roughness: 0.12, transparent: true, opacity: 0.7, depthWrite: false }),
    headlight: new THREE.MeshStandardMaterial({ color: 0xfff6df, emissive: 0xffe6a8, emissiveIntensity: 0.8, roughness: 0.2 }),
    plate: new THREE.MeshStandardMaterial({ color: 0xf3f3ee, roughness: 0.5 }),
    tail: new THREE.MeshStandardMaterial({ color: 0xcc2b3a, emissive: 0x8a1420, emissiveIntensity: 0.6, roughness: 0.4 }),
  };
}

type Materials = ReturnType<typeof createMaterials>;

function readFinish(m: Materials): Finish {
  return { body: m.paint.color.clone(), accent: m.accent.color.clone(), rim: m.rim.color.clone(), rimMetalness: m.rim.metalness, rimRoughness: m.rim.roughness };
}

function applyFinish(m: Materials, from: Finish, to: Finish, k: number) {
  m.paint.color.lerpColors(from.body, to.body, k);
  m.hatchPaint.color.copy(m.paint.color);
  m.accent.color.lerpColors(from.accent, to.accent, k);
  m.rim.color.lerpColors(from.rim, to.rim, k);
  m.rim.metalness = THREE.MathUtils.lerp(from.rimMetalness, to.rimMetalness, k);
  m.rim.roughness = THREE.MathUtils.lerp(from.rimRoughness, to.rimRoughness, k);
}

/** Builds the full material set once, then keeps store-driven colors live via refs. */
export function useTruckMaterials() {
  const bodyColor = useConfig((s) => s.bodyColor);
  const fenders = useConfig((s) => s.fenders);
  const wheels = useConfig((s) => s.wheels);
  const invalidate = useThree((s) => s.invalidate);

  const mats = useMemo(() => createMaterials(targetFinish(useConfig.getState())), []);
  const ease = useRef<{ tween: Tween; from: Finish; to: Finish } | null>(null);
  const applied = useRef(`${bodyColor}|${fenders}|${wheels}`);

  useLayoutEffect(() => {
    const key = `${bodyColor}|${fenders}|${wheels}`;
    if (key === applied.current) return;
    applied.current = key;
    const tween = new Tween(0, easeOutCubic);
    tween.retarget(1, PAINT_EASE_MS);
    ease.current = { tween, from: readFinish(mats), to: targetFinish({ bodyColor, fenders, wheels }) };
    invalidate();
  }, [bodyColor, fenders, wheels, mats, invalidate]);

  useFrame(() => {
    const e = ease.current;
    if (!e) return;
    const moving = e.tween.step();
    applyFinish(mats, e.from, e.to, e.tween.value);
    if (moving) invalidate();
    else ease.current = null;
  });

  return mats;
}

export type TruckMaterials = ReturnType<typeof useTruckMaterials>;
export interface MatsProps {
  mats: TruckMaterials;
}
